import { createFileRoute, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { getServiceCategoryAdmin, saveServiceCategory, generateServiceCategorySlug } from "@/lib/api/services.functions";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { FormActions } from "@/components/FormActions";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { ContentAdminTabs } from "@/components/admin/ContentAdminTabs";
import { SlugField } from "@/components/admin/SlugField";
import { SortableList } from "@/components/admin/SortableList";
import { ServiceItemEditor } from "@/components/admin/ServiceItemEditor";
import { MediaField } from "@/components/admin/MediaPicker";
import { LucideIconPicker } from "@/components/admin/LucideIconPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { imageFromService, type MediaSelection } from "@/lib/media/types";

type ServiceItemForm = { id: string; name: string; description: string; image: MediaSelection };

export const Route = createFileRoute("/admin/services/$id")({
  loader: ({ params }) => getServiceCategoryAdmin({ data: { id: params.id } }),
  component: EditServicePage,
});

function EditServicePage() {
  const cat = Route.useLoaderData();
  if (!cat) throw notFound();

  const [name, setName] = useState(cat.name);
  const [slug, setSlug] = useState(cat.slug);
  const [short, setShort] = useState(cat.short ?? "");
  const [intro, setIntro] = useState(cat.intro ?? "");
  const [image, setImage] = useState<MediaSelection>(imageFromService(cat));
  const [iconName, setIconName] = useState(cat.iconName ?? "");
  const [items, setItems] = useState<ServiceItemForm[]>(
    cat.items.map((i, idx) => ({
      id: i.id ?? `item-${idx}`,
      name: i.name,
      description: i.description ?? "",
      image: imageFromService(i),
    })),
  );
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await saveServiceCategory({
        data: {
          id: cat.id,
          name,
          slug,
          short,
          intro,
          imageMediaId: image.mediaId,
          imageUrl: image.url,
          iconName: iconName || null,
          items: items.map((item) => ({
            name: item.name,
            description: item.description,
            imageMediaId: item.image.mediaId,
            imageUrl: item.image.url,
          })),
        },
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPageShell>
      <AdminPageHeader title="Edit service category" />
      <ContentAdminTabs
        settingsLabel="Category settings"
        settingsForm={
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
            <SlugField
              title={name}
              slug={slug}
              onSlugChange={setSlug}
              onGenerate={() => generateServiceCategorySlug({ data: { name, excludeId: cat.id } })}
            />
            <div className="space-y-2"><Label>Short description</Label><Textarea value={short} onChange={(e) => setShort(e.target.value)} /></div>
            <div className="space-y-2"><Label>Intro</Label><Textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={4} /></div>
            <MediaField label="Category image" value={image} onChange={setImage} />
            <LucideIconPicker value={iconName} onChange={setIconName} />
          </div>
        }
        content={
          <div className="space-y-3">
            <Label>Service items</Label>
            <SortableList
              items={items}
              getId={(item) => item.id}
              onReorder={setItems}
              renderItem={(item, index) => (
                <ServiceItemEditor
                  name={item.name}
                  description={item.description}
                  image={item.image}
                  onNameChange={(name) => {
                    const next = [...items];
                    next[index] = { ...next[index], name };
                    setItems(next);
                  }}
                  onDescriptionChange={(description) => {
                    const next = [...items];
                    next[index] = { ...next[index], description };
                    setItems(next);
                  }}
                  onImageChange={(image) => {
                    const next = [...items];
                    next[index] = { ...next[index], image };
                    setItems(next);
                  }}
                  onDelete={() => setItems(items.filter((_, i) => i !== index))}
                />
              )}
            />
            <FormActions>
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  setItems([...items, { id: `new-${Date.now()}`, name: "", description: "", image: { mediaId: null, url: null } }])
                }
              >
                <Plus className="mr-2 h-4 w-4" /> Add item
              </Button>
            </FormActions>
          </div>
        }
      />
      <AdminFormFooter cancelTo="/admin/services" onSave={handleSave} saving={saving} />
    </AdminPageShell>
  );
}
